/** "Domain sync" launcher dialog — reconciles local subscription rows with
 *  the vendor-side state (URL / types / bearer hash / retryOnError), as a
 *  background task. Scope is optional (default = everything); the two
 *  destructive options — credential rotation and unmanaged-subscription
 *  deletion — are OFF by default and carry explicit warnings.
 */
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as integrationsApi from '@/api/integrations';
import * as subscriptionsApi from '@/api/notificationSubscriptions';
import { AppDialog } from '@/components/AppDialog';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { singlePageTenant } from '@/scope/pageTenants';

const ALL_RESTECHS = '';

interface Props {
  open: boolean;
  /** The page's operators (D9): the one is pre-selected, all are featured; see
   *  `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
  /** Fired with the queued background task's id (202 response). */
  onLaunched: (taskId: string) => void;
}

export function SubscriptionSyncDialog({ open, pageTenants, onClose, onLaunched }: Props) {
  const snackbar = useSnackbar();

  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [restech, setRestech] = useState<string>(ALL_RESTECHS);
  const [account, setAccount] = useState('');
  const [rotateCredentials, setRotateCredentials] = useState(false);
  const [deleteUnmanaged, setDeleteUnmanaged] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTenant(singlePageTenant(pageTenants));
    setRestech(ALL_RESTECHS);
    setAccount('');
    setRotateCredentials(false);
    setDeleteUnmanaged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const catalogQuery = useQuery({
    queryKey: ['integrations', 'catalog'],
    queryFn: () => integrationsApi.listCatalog(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const restechOptions = useMemo(
    () =>
      (catalogQuery.data?.results ?? []).filter(
        (e) => e.can_manage_notification_subscriptions,
      ),
    [catalogQuery.data],
  );

  const mutation = useMutation({
    mutationFn: (body: subscriptionsApi.SubscriptionSyncRequest) => subscriptionsApi.sync(body),
    onSuccess: (r) => {
      snackbar.show('Domain sync started — follow progress in Background Tasks.', 'success');
      onLaunched(r.task_id);
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Sync launch failed.', 'error'),
  });

  const submit = () => {
    mutation.mutate({
      ...(tenant ? { tour_operator_id: tenant.id } : {}),
      ...(restech ? { restech } : {}),
      ...(account.trim() ? { account: account.trim() } : {}),
      rotate_credentials: rotateCredentials,
      delete_unmanaged: deleteUnmanaged,
    });
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Domain sync"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color={rotateCredentials || deleteUnmanaged ? 'warning' : 'primary'}
            disabled={mutation.isPending}
            onClick={submit}
          >
            {mutation.isPending ? 'Starting…' : 'Start sync'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Reconciles every matched subscription with its vendor-side state — callback URL,
          notification types, credential hash and retry flag are compared and drift is repaired.
          Runs as a background task; every mutation is audited with the run&apos;s task id.
        </Typography>

        <TenantPicker
          value={tenant}
          featured={pageTenants}
          onChange={setTenant}
          label="Tenant (optional)"
          placeholder="All tenants"
        />

        <TextField
          select
          size="small"
          label="Booking system (optional)"
          value={restech}
          onChange={(e) => setRestech(e.target.value)}
          helperText="Limit the sync to one restech."
        >
          <MenuItem value={ALL_RESTECHS}>
            <em>All booking systems</em>
          </MenuItem>
          {restechOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Account (optional)"
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          helperText="Account slug; leave blank for all accounts."
        />

        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={rotateCredentials}
                onChange={(e) => setRotateCredentials(e.target.checked)}
              />
            }
            label="Rotate credentials"
          />
          <FormHelperText sx={{ mt: -0.5 }}>
            Reissues every matched subscription&apos;s API credential. The old key is deleted only
            after the vendor accepts the new bearer — but a failed rotation leaves that row
            failing until retried.
          </FormHelperText>
        </FormControl>

        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteUnmanaged}
                onChange={(e) => setDeleteUnmanaged(e.target.checked)}
              />
            }
            label="Delete unmanaged remote subscriptions"
          />
          <FormHelperText sx={{ mt: -0.5 }}>
            Deletes vendor-side subscriptions that have no local row. Guarded to URLs targeting
            our callback namespace — foreign subscriptions on a shared vendor key are never
            touched — but deletion is permanent.
          </FormHelperText>
        </FormControl>

        {(rotateCredentials || deleteUnmanaged) && (
          <Alert severity="warning">
            The selected options mutate vendor-side state beyond drift repair. Scope the sync as
            narrowly as possible.
          </Alert>
        )}
      </Stack>
    </AppDialog>
  );
}
